import axios from 'axios';
import { createUserWithEmailAndPassword, getAdditionalUserInfo, getRedirectResult, GoogleAuthProvider, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';
import { unregisterThisBrowser } from '../utils/webPush';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, withAuthProxyFallback } from '../firebase';
import { endSpoofingSession, getSpoofingStatus, startSpoofingSession } from '../services/adminApi';
import config from '../services/config';
import { getUserProfile } from '../services/userApi';
import { setCurrentOrgId } from '../services/auth/currentOrg';
import { CellCogEvents } from '../utils/analytics';
import { getFirstTouchHeader } from '../utils/firstTouch';
import NotificationSoundManager from '../utils/NotificationSoundManager';
import UserStreamManager from '../utils/UserStreamManager';

const AuthContext = createContext();

// LocalStorage key for profile cache
const USER_PROFILE_CACHE_KEY = 'cellcog_user_profile_cache';
export const ADMIN_FLAG_KEY = 'cellcog_is_admin';  // exported for PresenceProvider (an admin tab waits for its spoof status)
// Which auth page initiated a signInWithRedirect fallback (CEL-1086) — read
// back on return so the completion analytics fire with the right page.
const GOOGLE_REDIRECT_PAGE_KEY = 'cellcog_google_redirect_page';



// Create axios instance with proper base URL
const apiAxios = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  // Spoofing state
  const [spoofingStatus, setSpoofingStatus] = useState(null);
  const [checkingSpoofing, setCheckingSpoofing] = useState(false);
  const [isActualAdmin, setIsActualAdmin] = useState(false);



  const getCachedProfile = () => {
    try {
      const cachedProfileJson = localStorage.getItem(USER_PROFILE_CACHE_KEY);
      if (cachedProfileJson) {
        return JSON.parse(cachedProfileJson);
      }
    } catch (err) {
      console.error('Error parsing cached profile:', err);
    }
    return null;
  };

  const cacheProfile = (profile) => {
    try {
      localStorage.setItem(USER_PROFILE_CACHE_KEY, JSON.stringify(profile));
      if (profile.is_admin) {
        localStorage.setItem(ADMIN_FLAG_KEY, 'true');
        setIsActualAdmin(true);
      }
    } catch (err) {
      console.error('Error caching profile:', err);
    }
  };

  // PostHog identity (Aug 21 2026 posture, Nitish-approved): signed-in REAL
  // CUSTOMERS are identified by uid so product events join into per-account
  // journeys. Internal (@cellcog.ai/.com, the demo account included) never identify —
  // the loader already excludes them from capture entirely; this guard is
  // defense in depth. uid only, no person properties (email stays in our DB;
  // the uid is the join key everywhere).
  const syncAnalyticsIdentity = (profile) => {
    try {
      if (!window.posthog || typeof window.posthog.identify !== 'function') return;
      const uid = auth.currentUser?.uid;
      if (!uid || !profile) return;
      const email = String(profile.email || '').toLowerCase();
      if (/@cellcog\.(ai|com)$/.test(email)) return;
      // Spoofing defense (self-review catch): during an admin spoofing session
      // the fetched profile is the SPOOFED customer's (their email passes the
      // guard above) while auth.currentUser.uid is the ADMIN's — identify()
      // here would pollute analytics with a fake customer identity. The admin
      // flag persists through spoofing; admins never identify.
      if (localStorage.getItem('cellcog_is_admin') === 'true') return;
      window.posthog.identify(uid);
    } catch (e) { /* analytics must never break auth flows */ }
  };

  // CEL-3196 (Oct 2 2026): after createUserWithEmailAndPassword, the auth listener AND signup() both called this. The
  // listener's request reached the server first and was refused (throwaway inbox, IP cap, blocked domain): the server
  // deleted the identity and this browser signed out, the listener's throw went nowhere, and signup()'s own call then
  // found no current user, returned quietly, and the page treated the refusal as a successful signup (user landed on
  // Home, no message). Now every caller for the same user SHARES one request, and a refusal is remembered briefly so a
  // caller arriving after the sign-out still receives it.
  const sessionInflightRef = useRef(null); // { uid, promise }
  const lastRefusalRef = useRef(null); // { error, at }

  const createSessionCookie = async () => {
    const user = auth.currentUser;
    if (!user) {
      const r = lastRefusalRef.current;
      if (r && Date.now() - r.at < 60000) {
        lastRefusalRef.current = null;
        throw r.error;
      }
      return;
    }
    const current = sessionInflightRef.current;
    if (current && current.uid === user.uid) return current.promise;
    const promise = postSessionLogin(user);
    sessionInflightRef.current = { uid: user.uid, promise };
    try {
      return await promise;
    } finally {
      if (sessionInflightRef.current?.promise === promise) sessionInflightRef.current = null;
    }
  };

  const postSessionLogin = async (user) => {
    try {
      const token = await user.getIdToken();
      if (token) {
        const headers = { 'Authorization': `Bearer ${token}` };
        // Signup attribution (CEL-491/CEL-526): send stored first-touch data; the backend
        // stamps it ONCE for fresh accounts (immutable + 48h age guard server-side).
        const firstTouch = getFirstTouchHeader();
        if (firstTouch) {
          headers['X-CellCog-First-Touch'] = firstTouch;
        }
        await apiAxios.post('/cellcog/session/login', {}, { headers });
        lastRefusalRef.current = null;
      }
    } catch (error) {
      // Signup refusals (Oct 1 2026): the server refuses to CREATE an account for a throwaway inbox, a network over
      // its daily password-signup cap, or a blocked domain, and deletes the Firebase identity. Sign this browser out
      // and throw a coded error the auth pages turn into an inline notice (authErrorCopy.js).
      const refusal = error?.response?.status === 403 ? error?.response?.data?.error_type : null;
      if (['signup_disposable_email', 'signup_rate_limited', 'signup_domain_blocked'].includes(refusal)) {
        try {
          await signOut(auth);
        } catch (e) {
          /* the identity is already gone server-side */
        }
        const refused = new Error(error.response.data?.detail || 'Signup refused');
        refused.code = `cellcog/${refusal}`;
        lastRefusalRef.current = { error: refused, at: Date.now() };
        throw refused;
      }
      console.error('[AuthContext] Failed to create session cookie:', error);
    }
  };

  // Completes a signInWithRedirect round-trip (CEL-1086: popup-blocked
  // fallback). Google returns the user to the auth page in the SAME tab; the
  // popup .then() never ran, so the session cookie + analytics are minted here
  // instead. getRedirectResult resolves null when no redirect is pending, so
  // this is a no-op on normal page loads.
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (!result?.user) return;
        sessionStorage.setItem('cellcog_just_authenticated', 'true');
        await createSessionCookie();
        const page = sessionStorage.getItem(GOOGLE_REDIRECT_PAGE_KEY) || 'login';
        sessionStorage.removeItem(GOOGLE_REDIRECT_PAGE_KEY);
        // Google silently LOGS IN existing accounts / CREATES new ones — count
        // the event it really was, mirroring the popup handlers (TRACKING.md).
        const isNewUser = getAdditionalUserInfo(result)?.isNewUser;
        if (isNewUser) {
          CellCogEvents.signUp('google', { source_page: page, is_new_user: true });
        } else {
          CellCogEvents.login('google');
        }
      })
      .catch((error) => {
        const page = sessionStorage.getItem(GOOGLE_REDIRECT_PAGE_KEY) || 'login';
        sessionStorage.removeItem(GOOGLE_REDIRECT_PAGE_KEY);
        console.error('[AuthContext] Redirect sign-in completion failed:', error);
        CellCogEvents.authError(page, 'google', error?.code || 'unknown');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function signup(email, password) {
    return withAuthProxyFallback(() => createUserWithEmailAndPassword(auth, email, password))
      .then(async (result) => {
        await createSessionCookie();
        return result;
      });
  }

  function login(email, password) {
    return withAuthProxyFallback(() => signInWithEmailAndPassword(auth, email, password))
      .then(async (result) => {
        await createSessionCookie();
        return result;
      });
  }

  async function logout() {
    // Browser notifications (CEL-2490, journey review gap 3): forget THIS browser's push registration on the server
    // while the session is still valid, so the next account on this machine never receives this one's notifications.
    // Best-effort; the browser keeps its permission and re-registers at the next sign-in when that account is on.
    try {
      await unregisterThisBrowser();
    } catch (e) {
      /* never block sign-out on it */
    }
    try {
      await apiAxios.post('/cellcog/session/logout');
    } catch (error) {
      console.error('[AuthContext] Failed to clear session cookie:', error);
    }

    setUserProfile(null);
    setSpoofingStatus(null);
    setIsActualAdmin(false);
    localStorage.removeItem(USER_PROFILE_CACHE_KEY);
    localStorage.removeItem(ADMIN_FLAG_KEY);
    setCurrentOrgId(null);
    // Analytics identity unlink happens in the onAuthStateChanged handler
    // below (guarded on an actual signed-in -> signed-out transition), which
    // signOut() triggers — ONE reset per logout, covering every signout path.
    return signOut(auth);
  }

  function loginWithGoogle(page = 'login') {
    const provider = new GoogleAuthProvider();
    return withAuthProxyFallback(() => signInWithPopup(auth, provider))
      .catch((error) => {
        // Popup blocked -> full-page redirect fallback (CEL-1086). Resolves
        // undefined and navigates away; completion (session cookie + analytics)
        // happens in the getRedirectResult effect above when Google returns
        // the user here. Redirect is safe for us: authDomain is first-party
        // (cellcog.ai) with a transparent /__/ proxy, per Google's own
        // redirect-best-practices.
        if (error?.code === 'auth/popup-blocked') {
          sessionStorage.setItem(GOOGLE_REDIRECT_PAGE_KEY, page);
          return signInWithRedirect(auth, provider);
        }
        throw error;
      })
      .then(async (result) => {
        if (!result) return result; // redirect path — navigating away
        await createSessionCookie();
        return result;
      });
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  const fetchUserProfileWithRetry = async (retries = 3) => {
    setProfileLoading(true);
    setProfileError(null);

    const cachedProfile = getCachedProfile();
    // Organizations v3 (ring 0 read, Sep 30 2026): a saved profile from ANOTHER account on this browser (or an org the
    // person has since left) seeded a stale X-CellCog-Org on every load; 154 rejections from 2 browsers in a day. Use
    // the saved profile only when it is this signed-in person's; the fresh profile below always sets the org.
    const liveEmail = (auth.currentUser?.email || '').toLowerCase();
    const cachedIsMine = Boolean(cachedProfile && liveEmail && cachedProfile.email
      && cachedProfile.email.toLowerCase() === liveEmail);
    if (cachedProfile && cachedIsMine) {
      setUserProfile(cachedProfile);
      setCurrentOrgId(cachedProfile.organization?.id);
    } else if (cachedProfile) {
      setCurrentOrgId(null);
      localStorage.removeItem(USER_PROFILE_CACHE_KEY);
    }

    let attempt = 0;
    while (attempt < retries) {
      try {
        const response = await getUserProfile();
        const profile = response.data;
        setUserProfile(profile);
        setCurrentOrgId(profile.organization?.id);
        setProfileError(null);

        // Sync server-side sound notification setting to NotificationSoundManager
        const soundEnabled = profile.notification_settings?.sound_notification !== false;
        NotificationSoundManager.setMuted(!soundEnabled);

        cacheProfile(profile);

        setProfileLoading(false);
        return;
      } catch (err) {
        console.error(`Error fetching user profile (attempt ${attempt + 1}/${retries}):`, err);
        attempt++;

        if (attempt >= retries) {
          setProfileError(err);
          setProfileLoading(false);
          return;
        }

        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const refreshUserProfile = () => {
    if (currentUser) {
      fetchUserProfileWithRetry(3);
    }
  };

  const checkSpoofingStatus = async () => {
    if (!currentUser || !isActualAdmin) {
      setSpoofingStatus(null);
      return;
    }

    try {
      setCheckingSpoofing(true);
      const response = await getSpoofingStatus();
      setSpoofingStatus(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to check spoofing status:', error);
      }
      setSpoofingStatus(null);
    } finally {
      setCheckingSpoofing(false);
    }
  };

  const startSpoofing = async (userId) => {
    try {
      const response = await startSpoofingSession(userId);
      // Organizations v3: the admin's cached org must not ride the spoofed session (the server ignores it, but it is
      // noise and the wrong id); the next profile load stores the spoofed user's org.
      setCurrentOrgId(null);
      await checkSpoofingStatus();
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const endSpoofing = async () => {
    try {
      await endSpoofingSession();
      setSpoofingStatus(null);
      // Organizations v3: the reload must not start from the SPOOFED user's cached profile and org (prod Sep 29 2026:
      // six org_not_member rejections from an admin tab right after a spoof ended). The fresh profile restores both.
      setCurrentOrgId(null);
      localStorage.removeItem(USER_PROFILE_CACHE_KEY);
      window.location.href = '/admin/users';
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserProfileWithRetry(3);
    }
  }, [currentUser]);

  // Identity sync fires on every profile load (cached or fresh) — identify()
  // is idempotent, PostHog no-ops on an unchanged distinct id.
  useEffect(() => {
    if (userProfile) syncAnalyticsIdentity(userProfile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  // Auto-unblock: when the backend signals an onboarding status change (email verified,
  // consent completed), refresh the profile so blocked screens advance in EVERY open tab
  // without a manual "Check now" click. Server publishes USER_ONBOARDING_UPDATED on the
  // user stream from /user/verify-email and /user/complete-consent.
  useEffect(() => {
    if (!currentUser) return undefined;

    const unsubscribe = UserStreamManager.subscribe('USER_ONBOARDING_UPDATED', () => {
      fetchUserProfileWithRetry(3);
    });

    return unsubscribe;
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // NEW: Check spoofing when profile loads and user is admin
  useEffect(() => {
    if (currentUser && userProfile?.is_admin && !spoofingStatus && !checkingSpoofing) {
      checkSpoofingStatus();
    }
  }, [currentUser, userProfile?.is_admin]);

  // Poll spoofing status every 60 seconds when admin
  useEffect(() => {
    if (!currentUser || !isActualAdmin) return;

    checkSpoofingStatus();

    const interval = setInterval(() => {
      checkSpoofingStatus();
    }, 60000);

    return () => clearInterval(interval);
  }, [currentUser, isActualAdmin]);

  // Organizations v3 (CEL-3127 week read, Oct 5 2026): a spoof session that EXPIRES (the timer, not the Exit button)
  // left the spoofed customer's org in this tab; every later request sent it and was refused (~99% of [ORG_CTX] lines).
  // On a spoofing -> not-spoofing flip, forget that org and the cached profile and reload the admin's own profile.
  const wasSpoofingRef = useRef(false);
  useEffect(() => {
    const nowSpoofing = Boolean(spoofingStatus?.is_spoofing);
    if (wasSpoofingRef.current && !nowSpoofing) {
      setCurrentOrgId(null);
      localStorage.removeItem(USER_PROFILE_CACHE_KEY);
      if (currentUser) {
        fetchUserProfileWithRetry(3);
      }
    }
    wasSpoofingRef.current = nowSpoofing;
  }, [spoofingStatus?.is_spoofing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load admin flag from localStorage on mount
  useEffect(() => {
    const adminFlag = localStorage.getItem(ADMIN_FLAG_KEY);
    if (adminFlag === 'true') {
      setIsActualAdmin(true);
    }
  }, []);



  useEffect(() => {
    // Tracks whether THIS pageload ever saw a signed-in user, so the analytics
    // reset below fires ONLY on a real signed-in -> signed-out transition.
    // Without the guard, the initial onAuthStateChanged(null) on every
    // logged-out pageload would reset PostHog's anonymous distinct id —
    // fragmenting every marketing visitor into a new "person" per pageload
    // (deep-review catch, Aug 21 2026).
    let hadUser = false;
    const unsubscribe = onAuthStateChanged(auth, async user => {
      let shown = false;
      if (user) {
        // CEL-3196 round 2 (Oct 2 2026): the app learns about a user only AFTER the server accepts the session.
        // Setting currentUser first let AuthPage redirect to Home and MarketingRoute swap the shell before a signup
        // refusal (throwaway inbox, IP cap, blocked domain) came back, so SignUp unmounted and its notice never rendered.
        // A refusal surfaces through signup()/login() (shared request) on the page that is still mounted.
        let refused = false;
        try {
          await createSessionCookie();
        } catch (e) {
          refused = true;
        }
        if (!refused && auth.currentUser?.uid === user.uid) {
          setCurrentUser(user);
          shown = true;
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setSpoofingStatus(null);
        setIsActualAdmin(false);
        localStorage.removeItem(USER_PROFILE_CACHE_KEY);
        localStorage.removeItem(ADMIN_FLAG_KEY);
        // Real signout (this pageload had a user): unlink the analytics
        // identity so the next visitor on this browser doesn't inherit this
        // account's distinct id. Covers logout(), other-tab signout, expiry.
        if (hadUser) {
          try { if (window.posthog && window.posthog.reset) window.posthog.reset(); } catch (e) {}
        }
      }
      hadUser = shown;
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Connect user stream when authenticated with token refresh callback
  useEffect(() => {
    if (currentUser) {
      const connectUserStream = async () => {
        try {
          // Set token refresh callback
          UserStreamManager.setTokenCallback(async () => {
            // Force token refresh to get fresh token
            const token = await currentUser.getIdToken(true);
            return token;
          });

          // Connect with fresh token
          await UserStreamManager.connect();
        } catch (error) {
          console.error('[Auth] Failed to connect user stream:', error);
        }
      };

      connectUserStream();
    } else {
      // Disconnect on logout
      UserStreamManager.disconnect();
    }
  }, [currentUser]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      UserStreamManager.disconnect();
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    profileLoading,
    profileError,
    refreshUserProfile,
    hasCellCogAccess: userProfile?.onboarding_status === 'JOINED',
    signup,
    login,
    logout,
    loginWithGoogle,
    resetPassword,
    // Spoofing
    spoofingStatus,
    checkingSpoofing,
    isSpoofing: spoofingStatus?.is_spoofing || false,
    spoofedUser: spoofingStatus?.spoofed_user || null,
    checkSpoofingStatus,
    startSpoofing,
    endSpoofing,
    isAdmin: userProfile?.is_admin || false,
    canAccessCowork: true,
    // Early-access: can this user hire AI Employees? Backend-computed from the hire allowlist. Used to
    // hide the Agents↔AI Employees surface switcher entirely for users who can't explore that surface.
    canHireAiEmployees: userProfile?.can_hire_ai_employees || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
